import { useState, useEffect } from 'react';
import { api } from '../utils/api';

interface Notification {
  id: string;
  type: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  data?: any;
}

interface NotificationState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
}

export const useNotifications = () => {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    loadNotifications();
    setupWebSocket();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setState({
        notifications: response.data,
        loading: false,
        error: null
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load notifications'
      }));
    }
  };

  const setupWebSocket = () => {
    const ws = new WebSocket(process.env.REACT_APP_WS_URL || 'wss://lavernaibackend.onrender.com');
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'notification') {
        setState(prev => ({
          ...prev,
          notifications: [data.payload, ...prev.notifications]
        }));
      }
    };

    return () => {
      ws.close();
    };
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n =>
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications');
      setState(prev => ({
        ...prev,
        notifications: []
      }));
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  return {
    notifications: state.notifications,
    loading: state.loading,
    error: state.error,
    markAsRead,
    clearAll
  };
}; 